/**
 * appStructureGraph — the PURE derivation behind the "Mappa" builder view.
 *
 * Turns the app's entities + their bindings into a layered graph (3 lanes:
 * INTERFACCE → LOGICA/QUERY → DATI) with typed edges. Side-effect-free (no
 * React, no callbacks) so it is unit-testable in isolation; `AppStructureMap`
 * is the ReactFlow shell around it. Reuses `AppErDiagram`'s column helpers.
 */
import { columnTarget, fieldKindForColumn } from "@/components/apps/DataGrid";
import type {
  ColumnOut,
  DatasetOut,
  FormOut,
  InterfaceOut,
  PageOut,
  AppQueryOut,
  TableSummary,
} from "@/lib/api-apps";

export type Lane = "iface" | "query" | "data";
export type MapNodeKind = "page" | "form" | "assistant" | "rest" | "webapp" | "query" | "logic" | "table" | "dataset";
export type EdgeTone = "read" | "write" | "rel" | "dataset";

export interface StructureNode {
  id: string; // prefixed graph id (e.g. "t:<uuid>")
  entityId: string; // the raw entity id, for navigation
  lane: Lane;
  kind: MapNodeKind;
  label: string;
  sub?: string;
  badge?: { text: string; tone: "read" | "write" } | null;
  orphan?: boolean;
}
export interface StructureEdge {
  from: string;
  to: string;
  tone: EdgeTone;
}
export interface StructureGraph {
  nodes: StructureNode[];
  edges: StructureEdge[];
  adjacency: Map<string, Set<string>>;
}

export interface StructureInput {
  tables: TableSummary[];
  columnsByTable: Map<string, ColumnOut[]>;
  datasets: DatasetOut[];
  queries: AppQueryOut[];
  pages: PageOut[];
  forms: FormOut[];
  interfaces: InterfaceOut[];
}

/**
 * Derive the layered graph. Edges are only emitted between nodes that actually
 * exist (dangling targets are dropped), and a table/dataset/query with zero
 * edges is flagged `orphan` (dead weight the operator should notice).
 */
export function buildAppStructureGraph(input: StructureInput): StructureGraph {
  const nodes: StructureNode[] = [];
  const edges: StructureEdge[] = [];
  const ids = new Set<string>();
  const add = (n: StructureNode) => {
    nodes.push(n);
    ids.add(n.id);
  };

  // RIGHT — tables + datasets (the ER core).
  for (const t of input.tables) {
    const dataCols = (input.columnsByTable.get(t.id) ?? []).filter((c) => c.kind !== "system");
    add({ id: `t:${t.id}`, entityId: t.id, lane: "data", kind: "table", label: t.name, sub: `${dataCols.length} campi` });
  }
  for (const d of input.datasets) {
    add({ id: `d:${d.id}`, entityId: d.id, lane: "data", kind: "dataset", label: d.name, sub: "proiezione" });
  }
  for (const t of input.tables) {
    for (const c of input.columnsByTable.get(t.id) ?? []) {
      const k = fieldKindForColumn(c);
      if (k === "relation" || k === "relation_many") {
        const target = columnTarget(c);
        if (target && ids.has(`t:${target}`)) edges.push({ from: `t:${t.id}`, to: `t:${target}`, tone: "rel" });
      }
    }
  }
  for (const d of input.datasets) {
    if (ids.has(`t:${d.base_table_id}`)) edges.push({ from: `d:${d.id}`, to: `t:${d.base_table_id}`, tone: "dataset" });
  }

  // MIDDLE — ALL Queries (app-level AND page-owned). Page-owned ones get an edge
  // from their owning page (added below, once page nodes exist), so the operator
  // sees the whole query surface, not just the app-level handful.
  for (const q of input.queries) {
    const isWrite = q.source_kind === "logic";
    add({
      id: `q:${q.id}`,
      entityId: q.id,
      lane: "query",
      kind: isWrite ? "logic" : "query",
      label: q.name,
      sub: q.source_kind,
      badge: { text: isWrite ? "W" : "R", tone: isWrite ? "write" : "read" },
    });
    const spec = q.spec ?? {};
    const tid = spec.table_id as string | undefined;
    const did = spec.dataset_id as string | undefined;
    if ((q.source_kind === "table" || q.source_kind === "logic") && tid && ids.has(`t:${tid}`))
      edges.push({ from: `q:${q.id}`, to: `t:${tid}`, tone: isWrite ? "write" : "read" });
    if (q.source_kind === "dataset" && did && ids.has(`d:${did}`)) edges.push({ from: `q:${q.id}`, to: `d:${did}`, tone: "read" });
  }

  // LEFT — pages (web), forms, chat/voce assistants.
  for (const p of input.pages) {
    add({ id: `page:${p.id}`, entityId: p.id, lane: "iface", kind: "page", label: p.title || p.slug, sub: p.is_home ? "home" : "pagina" });
    for (const src of p.ast?.queries ?? []) {
      if (src.source_kind === "table" && src.table_id && ids.has(`t:${src.table_id}`))
        edges.push({ from: `page:${p.id}`, to: `t:${src.table_id}`, tone: "read" });
      else if (src.source_kind === "dataset" && src.dataset_id && ids.has(`d:${src.dataset_id}`))
        edges.push({ from: `page:${p.id}`, to: `d:${src.dataset_id}`, tone: "read" });
      else if (src.source_kind === "query" && src.query_id && ids.has(`q:${src.query_id}`))
        edges.push({ from: `page:${p.id}`, to: `q:${src.query_id}`, tone: "read" });
    }
  }
  for (const f of input.forms) {
    add({ id: `form:${f.id}`, entityId: f.id, lane: "iface", kind: "form", label: f.name, sub: "form", badge: { text: "W", tone: "write" } });
    if (ids.has(`t:${f.table_id}`)) edges.push({ from: `form:${f.id}`, to: `t:${f.table_id}`, tone: "write" });
  }
  for (const i of input.interfaces) {
    if (i.kind === "chat" || i.kind === "voice") {
      add({ id: `iface:${i.id}`, entityId: i.id, lane: "iface", kind: "assistant", label: i.name, sub: i.kind === "voice" ? "voce" : "chat" });
    } else if (i.kind === "rest") {
      add({ id: `iface:${i.id}`, entityId: i.id, lane: "iface", kind: "rest", label: i.name, sub: "REST" });
      // A REST interface's config lists endpoints over a table/dataset/query —
      // draw an edge to each exposed target so it reads like any other consumer.
      const eps = (i.config?.endpoints as Array<{ source?: string; target_id?: string }> | undefined) ?? [];
      for (const ep of eps) {
        if (!ep.target_id) continue;
        if (ep.source === "table" && ids.has(`t:${ep.target_id}`)) edges.push({ from: `iface:${i.id}`, to: `t:${ep.target_id}`, tone: "read" });
        else if (ep.source === "dataset" && ids.has(`d:${ep.target_id}`)) edges.push({ from: `iface:${i.id}`, to: `d:${ep.target_id}`, tone: "read" });
        else if (ep.source === "query" && ids.has(`q:${ep.target_id}`)) edges.push({ from: `iface:${i.id}`, to: `q:${ep.target_id}`, tone: "read" });
      }
    }
  }

  // Page-owned queries → an edge from their owning page (now that both exist).
  for (const q of input.queries) {
    if (q.page_id && ids.has(`q:${q.id}`) && ids.has(`page:${q.page_id}`))
      edges.push({ from: `page:${q.page_id}`, to: `q:${q.id}`, tone: "read" });
  }

  // Dedup edges (a page↔query link can arrive from both the AST and ownership).
  const seenEdge = new Set<string>();
  const deduped: StructureEdge[] = [];
  for (const e of edges) {
    const k = `${e.from}|${e.to}|${e.tone}`;
    if (seenEdge.has(k)) continue;
    seenEdge.add(k);
    deduped.push(e);
  }
  edges.length = 0;
  edges.push(...deduped);

  // Orphans — a table/dataset/query with no edge at all.
  const degree = new Map<string, number>();
  for (const e of edges) {
    degree.set(e.from, (degree.get(e.from) ?? 0) + 1);
    degree.set(e.to, (degree.get(e.to) ?? 0) + 1);
  }
  for (const n of nodes) {
    if ((n.kind === "table" || n.kind === "dataset" || n.kind === "query" || n.kind === "logic") && !degree.get(n.id)) n.orphan = true;
  }

  return { nodes, edges, adjacency: buildAdjacency(edges) };
}

/** Undirected 1-hop adjacency, for Focus mode + neighbour-expansion filters. */
export function buildAdjacency(edges: StructureEdge[]): Map<string, Set<string>> {
  const adjacency = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    adjacency.get(a)!.add(b);
  };
  for (const e of edges) {
    link(e.from, e.to);
    link(e.to, e.from);
  }
  return adjacency;
}

export interface MapFilters {
  /** case-insensitive substring on node labels; keeps matches + their neighbours */
  query?: string;
  /** keep only the orphans (the "solo problemi" backstop) */
  onlyProblems?: boolean;
  /** lanes to drop entirely */
  hiddenLanes?: Set<Lane>;
}

/**
 * Filter the graph to a legible subgraph (Slice 2 — anti-clutter at scale).
 * Filters HIDE nodes (vs Focus mode, which only dims): lane toggles + "solo
 * problemi" narrow the candidate set, then a search query keeps the matches PLUS
 * their 1-hop neighbours (so a hit keeps its context). Edges survive only when
 * both endpoints do. Returns a fresh `StructureGraph` (adjacency rebuilt).
 */
export function applyMapFilters(g: StructureGraph, f: MapFilters): StructureGraph {
  const hidden = f.hiddenLanes ?? new Set<Lane>();
  let candidates = g.nodes.filter((n) => !hidden.has(n.lane));
  if (f.onlyProblems) candidates = candidates.filter((n) => n.orphan);

  const candidateIds = new Set(candidates.map((n) => n.id));
  const q = (f.query ?? "").trim().toLowerCase();
  let keep: Set<string>;
  if (!q) {
    keep = candidateIds;
  } else {
    const matches = candidates.filter((n) => n.label.toLowerCase().includes(q)).map((n) => n.id);
    keep = new Set(matches);
    for (const id of matches) {
      for (const nb of g.adjacency.get(id) ?? []) if (candidateIds.has(nb)) keep.add(nb);
    }
  }

  const nodes = g.nodes.filter((n) => keep.has(n.id));
  const edges = g.edges.filter((e) => keep.has(e.from) && keep.has(e.to));
  return { nodes, edges, adjacency: buildAdjacency(edges) };
}

// ── Navigator: semantic-zoom views (Slice 4) ──────────────────────────────────
// The flat 3-lane graph is a hairball on a real app. Instead the map is a
// NAVIGATOR: the overview shows only interface containers; you drill INTO one to
// see its scoped subgraph; data is opened on demand. These pure helpers derive
// each view's subgraph from the one full graph.

export const WEBAPP_GROUP_ID = "group:webapp";
export const FORM_GROUP_ID = "group:form";

export type MapView =
  | { kind: "overview" }
  | { kind: "interface"; id: string; label: string }
  | { kind: "data" }
  | { kind: "matrix" };

/**
 * Overview — interface containers ONLY (web pages folded into one "Web app" card,
 * forms into one "Form" card; chat/voice/rest stay as single cards). No query/data
 * nodes and no edges: it's the clean entry point that stays legible at any scale.
 */
export function overviewGraph(full: StructureGraph): StructureGraph {
  const iface = full.nodes.filter((n) => n.lane === "iface");
  const pages = iface.filter((n) => n.kind === "page");
  const forms = iface.filter((n) => n.kind === "form");
  const singles = iface.filter((n) => n.kind !== "page" && n.kind !== "form");
  const nodes: StructureNode[] = [];
  if (pages.length) nodes.push({ id: WEBAPP_GROUP_ID, entityId: "", lane: "iface", kind: "webapp", label: "Web app", sub: `${pages.length} pagine` });
  nodes.push(...singles);
  if (forms.length) nodes.push({ id: FORM_GROUP_ID, entityId: "", lane: "iface", kind: "form", label: "Form", sub: `${forms.length} form` });
  return { nodes, edges: [], adjacency: new Map() };
}

/**
 * Forward-reachable subgraph from a seed set: follow edges in the consume
 * direction (interface → query → table) only. Direction bounds the walk — it never
 * climbs back into OTHER interfaces/pages, so an interface's scope stays about that
 * interface. `includeRelations: false` also drops table→table `rel` edges from both
 * the walk AND the output — so an interface drill shows only the tables it actually
 * touches (relations are the Vista Dati's job), never the whole data neighbourhood.
 * Returns a fresh graph (adjacency rebuilt).
 */
export function scopeForward(full: StructureGraph, seeds: Set<string>, opts?: { includeRelations?: boolean }): StructureGraph {
  const includeRel = opts?.includeRelations ?? true;
  const usable = includeRel ? full.edges : full.edges.filter((e) => e.tone !== "rel");
  const out = new Map<string, string[]>();
  for (const e of usable) {
    if (!out.has(e.from)) out.set(e.from, []);
    out.get(e.from)!.push(e.to);
  }
  const keep = new Set(seeds);
  const queue = [...seeds];
  while (queue.length) {
    const id = queue.shift()!;
    for (const to of out.get(id) ?? []) if (!keep.has(to)) { keep.add(to); queue.push(to); }
  }
  const nodes = full.nodes.filter((n) => keep.has(n.id));
  const edges = usable.filter((e) => keep.has(e.from) && keep.has(e.to));
  return { nodes, edges, adjacency: buildAdjacency(edges) };
}

/** Scope INTO one interface (or the synthetic Web app / Form group). */
export function interfaceScopeGraph(full: StructureGraph, id: string): StructureGraph {
  let seeds: Set<string>;
  if (id === WEBAPP_GROUP_ID) seeds = new Set(full.nodes.filter((n) => n.kind === "page").map((n) => n.id));
  else if (id === FORM_GROUP_ID) seeds = new Set(full.nodes.filter((n) => n.kind === "form").map((n) => n.id));
  else seeds = new Set([id]);
  // No relation-expansion inside an interface — show only what it directly touches.
  return scopeForward(full, seeds, { includeRelations: false });
}

// ── Matrix view (alternate reading — "who touches what", no crossing edges) ────
// A grid instead of a graph: rows = data (tables/datasets), columns = the
// consumers that read/write them (queries, forms, REST). A cell is a read/write
// dot or empty. It scales where the graph turns into a hairball — the eye scans a
// row ("who uses deals?") or a column ("what does close_deal touch?") with no lines.

export interface MatrixColumn {
  id: string;
  entityId: string;
  label: string;
  kind: MapNodeKind;
}
export interface MatrixRow {
  id: string;
  entityId: string;
  label: string;
  kind: MapNodeKind;
  orphan: boolean;
  /** one entry per column, in `columns` order; null = no relationship */
  cells: ("read" | "write" | null)[];
}
export interface AppMatrix {
  columns: MatrixColumn[];
  rows: MatrixRow[];
}

// Column grouping order: logic/query first, then forms, then REST, then anything else.
const COLUMN_RANK: Partial<Record<MapNodeKind, number>> = { query: 0, logic: 0, form: 1, rest: 2 };

/**
 * Derive the consumer×data matrix (PURE). Columns are exactly the nodes that have
 * a read/write edge into a data node (so no empty columns); rows are ALL data nodes
 * (empty rows surface orphan tables). A cell takes the strongest tone — write wins
 * over read — across that consumer's edges to that table.
 */
export function buildMatrix(full: StructureGraph): AppMatrix {
  const byId = new Map(full.nodes.map((n) => [n.id, n]));
  const isData = (id: string) => byId.get(id)?.lane === "data";
  const rw = full.edges.filter((e) => (e.tone === "read" || e.tone === "write") && isData(e.to));

  const colIds = new Set(rw.map((e) => e.from));
  const columns: MatrixColumn[] = [...colIds]
    .map((id) => byId.get(id))
    .filter((n): n is StructureNode => !!n)
    .sort((a, b) => (COLUMN_RANK[a.kind] ?? 9) - (COLUMN_RANK[b.kind] ?? 9) || a.label.localeCompare(b.label))
    .map((n) => ({ id: n.id, entityId: n.entityId, label: n.label, kind: n.kind }));
  const colIndex = new Map(columns.map((c, i) => [c.id, i]));

  const dataNodes = full.nodes
    .filter((n) => n.lane === "data")
    .sort((a, b) => a.kind.localeCompare(b.kind) || a.label.localeCompare(b.label));

  const rows: MatrixRow[] = dataNodes.map((r) => {
    const cells: (("read" | "write") | null)[] = columns.map(() => null);
    for (const e of rw) {
      if (e.to !== r.id) continue;
      const i = colIndex.get(e.from);
      if (i === undefined) continue;
      if (e.tone === "write" || cells[i] === null) cells[i] = e.tone as "read" | "write";
    }
    return { id: r.id, entityId: r.entityId, label: r.label, kind: r.kind, orphan: !!r.orphan, cells };
  });

  return { columns, rows };
}
